import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';

export default function NYCLaunchPage() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    reasons: []
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const reasonOptions = [
    { id: 'called', label: 'I feel called to help build' },
    { id: 'serve', label: 'I want to serve and use my gift' },
    { id: 'reach', label: 'I want to help reach people in NYC' },
    { id: 'praying', label: "I've been praying for something like this" }
  ];

  const handleReasonChange = (reasonId, checked) => {
    setFormData(prev => ({
      ...prev,
      reasons: checked
        ? [...prev.reasons, reasonId]
        : prev.reasons.filter(r => r !== reasonId)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone) {
      toast.error('Please fill in all contact information fields');
      return;
    }

    if (formData.reasons.length === 0) {
      toast.error('Please select at least one reason');
      return;
    }

    setSubmitting(true);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/launch-team`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitted(true);
        toast.success('Welcome to the NYC Launch Team!');
      } else {
        toast.error(data.message || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Submission error:', error);
      toast.error('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-white" data-testid="nyc-launch-page">
        <div className="mx-auto max-w-3xl px-6 sm:px-10 py-24 sm:py-32 lg:py-40">
          <div className="text-center">
            <div className="mb-8">
              <div className="w-16 h-px bg-gray-300 mx-auto mb-8" />
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-medium text-gray-900 leading-tight italic">
                Thank You
              </h1>
              <div className="w-16 h-px bg-gray-300 mx-auto mt-8" />
            </div>

            <p className="text-[15px] text-gray-600 font-body leading-relaxed mt-8 max-w-xl mx-auto">
              Thank you for your interest in joining. A member of our team will reach out to you soon. We look forward to building with you.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="nyc-launch-page">
      <div className="mx-auto max-w-3xl px-6 sm:px-10 py-24 sm:py-32 lg:py-40">
        {/* Header */}
        <div className="text-center mb-16">
          <p className="text-[11px] uppercase tracking-[0.25em] text-gray-400 font-body font-medium mb-6">
            New York City
          </p>
          <div className="w-16 h-px bg-gray-300 mx-auto mb-8" />
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-medium text-gray-900 leading-tight italic">
            Join the Launch Team
          </h1>
          <div className="w-16 h-px bg-gray-300 mx-auto mt-8" />
        </div>

        {/* Intro Text */}
        <div className="mb-12 text-center">
          <p className="text-[15px] text-gray-600 font-body leading-relaxed max-w-2xl mx-auto">
            We are in New York City. We are gathering a core team of believers who want to build something meaningful for the kingdom in the city. If that sounds like you, come and build with us.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-10" data-testid="nyc-launch-form">
          {/* Reasons Section */}
          <div className="border-t border-gray-100 pt-10">
            <Label className="block text-[13px] uppercase tracking-[0.15em] text-gray-700 font-body font-medium mb-6">
              What makes you want to be part of the launch team?
            </Label>
            <div className="space-y-4">
              {reasonOptions.map((option) => (
                <div key={option.id} className="flex items-start space-x-3" data-testid={`reason-option-${option.id}`}>
                  <Checkbox
                    id={option.id}
                    checked={formData.reasons.includes(option.id)}
                    onCheckedChange={(checked) => handleReasonChange(option.id, checked)}
                    className="mt-1"
                  />
                  <label
                    htmlFor={option.id}
                    className="text-[15px] text-gray-700 font-body leading-relaxed cursor-pointer"
                  >
                    {option.label}
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Contact Information */}
          <div className="border-t border-gray-100 pt-10">
            <Label className="block text-[13px] uppercase tracking-[0.15em] text-gray-700 font-body font-medium mb-6">
              Contact Information
            </Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="firstName" className="block text-[12px] uppercase tracking-[0.1em] text-gray-500 font-body mb-2">
                  First Name
                </Label>
                <Input
                  id="firstName"
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  className="w-full"
                  data-testid="first-name-input"
                  required
                />
              </div>

              <div>
                <Label htmlFor="lastName" className="block text-[12px] uppercase tracking-[0.1em] text-gray-500 font-body mb-2">
                  Last Name
                </Label>
                <Input
                  id="lastName"
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  className="w-full"
                  data-testid="last-name-input"
                  required
                />
              </div>

              <div>
                <Label htmlFor="email" className="block text-[12px] uppercase tracking-[0.1em] text-gray-500 font-body mb-2">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full"
                  data-testid="email-input"
                  required
                />
              </div>

              <div>
                <Label htmlFor="phone" className="block text-[12px] uppercase tracking-[0.1em] text-gray-500 font-body mb-2">
                  Phone Number
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full"
                  data-testid="phone-input"
                  required
                />
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="pt-6 text-center">
            <Button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center h-12 px-10 border border-gray-900 bg-transparent text-gray-900 font-body text-[13px] font-medium uppercase tracking-[0.15em] hover:bg-gray-900 hover:text-white transition-colors duration-200 disabled:opacity-50"
              data-testid="submit-button"
            >
              {submitting ? 'Submitting...' : 'Join the Team'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
